import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  AddCrossTinyIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  Body2,
  ButtonGroup,
  Caption1,
  Choose,
  ComboBox,
  ConfirmDialog,
  Header3,
  IconButton,
  InfoIcon,
  Input,
  Tooltip,
  TrashIcon,
} from "@activecollab/components";
import classNames from "classnames";
import { Field } from "formik";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import {
  getConfigOption,
  getDefaultWeeklyCapacity,
  getPersonalizedDateFormat,
  getRawConfigOption,
  getTimeWorkdays,
} from "~/store/selectors/selector.configOptions";
import { getActiveJobTypesArrayOfIdAndNameSelector } from "~/store/selectors/selector.jobTypes";
import { formatDateSmart, shortDayNames } from "~/utils/date";
import { preventNegativeAndEKeyPress, validateWorkWeekHours } from "~/utils/utils";
import { WorkWeekHours } from "./WorkWeekHours";
import UserLink from "../UserLink/UserLink";

const UserRatesAndCapacityForm = ({
  allUserHourlyRates,
  defaultCurrency,
  openDeleteDialog,
  setFieldValue,
  setOpenDeleteDialog,
  setOpenAddHourlyRateDialog,
  setSelectedHourlyRateId,
  values,
  workWeekData,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expandRatesHistory, setExpandRatesHistory] = useState(false);
  const [openWarningDialog, setOpenWarningDialog] = useState(false);

  const systemWorkdays = useSelector(getTimeWorkdays);
  const systemWeeklyWorkerCapacity = useSelector((state) => getConfigOption(state, `weekly_worker_capacity`))?.toJS();
  const jobTypes = useSelector(getActiveJobTypesArrayOfIdAndNameSelector);
  const systemTotalCapacity = useSelector(getDefaultWeeklyCapacity);
  const systemFirstDayOfTheWeek = useSelector((state) => getRawConfigOption(state, `time_first_week_day`));
  const dateFormat = useSelector((state) => getPersonalizedDateFormat(state));

  const days = useMemo(() => {
    const weeklyWorkerCapacity = workWeekData?.capacity;
    if (weeklyWorkerCapacity) {
      const days = weeklyWorkerCapacity.map((capacity, index) => {
        const disabled = !systemWorkdays.includes(index);
        const capacityValue = typeof capacity === "number" ? capacity / 60 : capacity;
        return {
          label: shortDayNames[index],
          value: capacityValue,
          disabled,
          isValid: validateWorkWeekHours(capacityValue),
        };
      });
      if (systemFirstDayOfTheWeek) {
        const sunday = days.shift();
        days.push(sunday);
      }

      return days;
    }
    return [];
  }, [workWeekData?.capacity, systemFirstDayOfTheWeek, systemWorkdays]);

  const resolveWeeklyCapacity = useCallback(
    (data) => {
      const workCapacity = data?.map((v) => (typeof v.value === "number" ? v.value * 60 : ""));
      if (systemFirstDayOfTheWeek) {
        const sunday = workCapacity.pop();
        workCapacity.unshift(sunday);
      }

      return workCapacity;
    },
    [systemFirstDayOfTheWeek]
  );

  const resolveRatesStatus = useCallback(
    (validFrom) => {
      const now = moment().unix();
      if (allUserHourlyRates.length > 0) {
        const currentRate = allUserHourlyRates.find((rate) => rate.valid_from <= now);
        const upcomingRate = allUserHourlyRates.find((rate) => rate.valid_from > now);
        if (!currentRate && !upcomingRate) {
          return null;
        }
        if (currentRate?.valid_from === validFrom) {
          return t("Current");
        }
        if (currentRate?.valid_from < validFrom || (upcomingRate?.valid_from >= validFrom && validFrom > now)) {
          return t("Upcoming");
        }
      }
    },
    [allUserHourlyRates, t]
  );

  const toggleExpandRatesHistory = useCallback(() => {
    setExpandRatesHistory(!expandRatesHistory);
  }, [expandRatesHistory]);

  const shouldRenderTrashIcon = useCallback((createdOn) => {
    return moment().diff(moment(createdOn * 1000), "days") < 90;
  }, []);

  const handleKeyDownOnInput = useCallback((e) => {
    preventNegativeAndEKeyPress(e);
  }, []);

  const handleOpenAddHourlyRateDialog = useCallback(() => {
    setOpenAddHourlyRateDialog(true);
  }, [setOpenAddHourlyRateDialog]);

  const isUpcoming = useCallback((validFrom) => resolveRatesStatus(validFrom) === "Upcoming", [resolveRatesStatus]);

  const resolveTitle = useCallback(
    (name) => {
      const updatedByName = name ? name : "N/A";
      return t("Added by ") + updatedByName;
    },
    [t]
  );

  const handleOpenWarningDialog = useCallback(() => {
    setOpenWarningDialog(true);
  }, []);

  const closeWarningDialog = useCallback(() => {
    setOpenWarningDialog(false);
  }, []);

  const goToSystemSettings = useCallback(() => {
    navigate(`/system-settings/workday`);
  }, [navigate]);

  const resolveTextColor = useMemo(() => {
    const allInvalidValues = values.weeklyWorkerCapacity?.every((day) => day === 0);
    return allInvalidValues ? "alert" : "tertiary";
  }, [values]);

  return (
    <>
      <Field name="weeklyWorkerCapacity">
        {() => {
          return (
            <>
              <Caption1 weight="bold">{t("Weekly Capacity")}</Caption1>
              <ButtonGroup className="tw-my-2">
                <Choose
                  className="button-group-choose"
                  type="button"
                  active={values?.capacity_source === "default"}
                  onClick={() => {
                    setFieldValue("weeklyWorkerCapacity", systemWeeklyWorkerCapacity);
                    setFieldValue("capacity_source", "default");
                  }}
                >
                  {t("Default")} ({`${systemTotalCapacity}h`})
                </Choose>
                <Choose
                  type="button"
                  className="button-group-choose"
                  active={values?.capacity_source === "custom"}
                  onClick={() => {
                    setFieldValue("weeklyWorkerCapacity", workWeekData?.capacity);
                    setFieldValue("capacity_source", "custom");
                  }}
                >
                  {t("Custom")}
                </Choose>
              </ButtonGroup>
              {values?.capacity_source === "default" ? (
                <>
                  <Caption1
                    color="tertiary"
                    lineHeight="loose"
                  >
                    {t("Default daily capacity can be set in System Settings.")}
                  </Caption1>
                  <Caption1
                    className="activity-link hover:tw-cursor-pointer"
                    onClick={handleOpenWarningDialog}
                  >
                    {t("Manage default daily capacity")}
                  </Caption1>
                </>
              ) : (
                <div className="tw-mt-6">
                  <WorkWeekHours
                    days={days}
                    onChange={(data) => setFieldValue("weeklyWorkerCapacity", resolveWeeklyCapacity(data))}
                    isValid={validateWorkWeekHours}
                  />
                  <Caption1
                    color="tertiary"
                    className="tw-mt-2"
                  >
                    {t("Enter a value that ends in either .00, .25, .5, or .75.")}
                  </Caption1>
                  <Caption1 color={resolveTextColor}>{t("At least one value must be greater than 0")}.</Caption1>
                </div>
              )}
            </>
          );
        }}
      </Field>
      <Field name="selectedJobTypes">
        {() => {
          return (
            <div className="tw-mt-6">
              <Caption1 weight="bold">{t("Available Job Types")}</Caption1>
              <ComboBox
                type="multiple"
                placeholder={t("Choose...")}
                inputWrapperClassName="tw-mt-2"
                selected={Object.keys(values.selectedJobTypes).map((id) => +id)}
                onChange={(ids) => {
                  setFieldValue(
                    "selectedJobTypes",
                    ids.reduce((acc, id) => {
                      return { ...acc, [id]: true };
                    }, {})
                  );
                }}
                options={jobTypes}
                disableSearch
                noResultText="No result"
              />
              <Caption1
                color="tertiary"
                className="tw-mt-2"
              >
                {t("If none is selected, all jobs will be available.")}
              </Caption1>
            </div>
          );
        }}
      </Field>
      <Field name="internalHourlyRate">
        {({ field }) => {
          return (
            <div className="tw-mt-6">
              <Caption1 weight="bold">{t("Internal Hourly Rate")}</Caption1>
              <div className="tw-flex tw-items-center tw-gap-1 tw-mt-2">
                <Input
                  min={0}
                  className="tw-w-[70px] input-without-arrows"
                  type="number"
                  onKeyDown={handleKeyDownOnInput}
                  {...field}
                />
                <Body2 color="tertiary">{defaultCurrency?.code}</Body2>
                <Tooltip title={t("This is the default currency set in System Settings")}>
                  <InfoIcon />
                </Tooltip>
              </div>
              <Caption1
                color="tertiary"
                className="tw-mt-2"
              >
                {t("Internal hourly rate shows employee's cost per hour for your company")}.
              </Caption1>
            </div>
          );
        }}
      </Field>
      {allUserHourlyRates?.length > 0 ? (
        <>
          <div className="tw-flex tw-justify-between tw-items-center tw-pr-2 tw-mb-2 tw-mt-6">
            <div
              className="tw-flex tw-items-center tw-cursor-pointer"
              onClick={toggleExpandRatesHistory}
            >
              {expandRatesHistory ? (
                <ArrowUpIcon fill="var(--color-primary)" />
              ) : (
                <ArrowDownIcon fill="var(--color-primary)" />
              )}
              <Header3 color="theme-primary">{t("Rates History")}</Header3>
            </div>
            {expandRatesHistory ? (
              <Tooltip title={t("Add previous or future rate")}>
                <IconButton
                  type="button"
                  variant="text gray"
                  size="small"
                  onClick={handleOpenAddHourlyRateDialog}
                >
                  <AddCrossTinyIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          </div>
          {expandRatesHistory ? (
            <div data-testid="rates-history">
              {allUserHourlyRates.map((rate, index) => (
                <div
                  key={rate.id}
                  data-testid={`hourly-rates-item-${rate.id}`}
                  className={classNames(
                    "tw-rounded-lg tw-cursor-default tw-h-10 tw-px-2 tw-flex tw-items-center tw-justify-between tw-gap-2",
                    {
                      "tw-bg-theme-200": index % 2 === 0,
                    }
                  )}
                >
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Body2
                      color={isUpcoming(rate.valid_from) ? "tertiary" : "secondary"}
                      className={classNames("tw-flex tw-w-20", {
                        "tw-italic": isUpcoming(rate.valid_from),
                      })}
                    >
                      <span className="tw-truncate">
                        {rate.rate} {defaultCurrency?.code}/h
                      </span>
                    </Body2>
                    <Body2
                      color={isUpcoming(rate.valid_from) ? "tertiary" : "secondary"}
                      className={classNames("tw-w-[100px]", {
                        "tw-italic": isUpcoming(rate.valid_from),
                      })}
                    >
                      {formatDateSmart(rate.valid_from, dateFormat)}
                    </Body2>
                    {resolveRatesStatus(rate.valid_from) && (
                      <Body2
                        data-testid={`rate-status-${rate.id}`}
                        color={isUpcoming(rate.valid_from) ? "tertiary" : "secondary"}
                        className={classNames({
                          "tw-italic": isUpcoming(rate.valid_from),
                        })}
                      >
                        {resolveRatesStatus(rate.valid_from)}
                      </Body2>
                    )}
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Tooltip title={resolveTitle(rate.updated_by_name)}>
                      <span className="tw-flex tw-items-center tw-cursor-help">
                        <UserLink
                          id={rate.updated_by_id}
                          notInteractive
                          hideName
                          className="tw-px-4"
                          name={rate.updated_by_name}
                          context="user rates and capacity form"
                        />
                      </span>
                    </Tooltip>

                    <IconButton
                      type="button"
                      variant="text gray"
                      size="small"
                      className={classNames({
                        "tw-invisible": !shouldRenderTrashIcon(rate.created_on),
                      })}
                      onClick={() => {
                        setSelectedHourlyRateId(rate.id);
                        setOpenDeleteDialog(!openDeleteDialog);
                      }}
                    >
                      <TrashIcon />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
      {openWarningDialog ? (
        <ConfirmDialog
          open={openWarningDialog}
          onCancel={closeWarningDialog}
          dialogTitle="Go to System Settings"
          dialogContent="Leaving this page will discard your progress."
          confirmBtnText="Discard Progress"
          onConfirm={goToSystemSettings}
        />
      ) : null}
    </>
  );
};

UserRatesAndCapacityForm.propTypes = {
  allUserHourlyRates: PropTypes.array,
  defaultCurrency: PropTypes.object,
  openDeleteDialog: PropTypes.bool,
  setFieldValue: PropTypes.func,
  setOpenAddHourlyRateDialog: PropTypes.func,
  setOpenDeleteDialog: PropTypes.func,
  setSelectedHourlyRateId: PropTypes.func,
  values: PropTypes.object,
  workWeekData: PropTypes.object,
};

export default UserRatesAndCapacityForm;
